import { useApolloClient } from "@apollo/client";
import React, { ReactElement, useState } from "react";
import {
    FormFieldReplacementInput,
    FormFieldType,
    SearchUsersDocument,
    SearchUsersQuery, User, useSearchUsersQuery,
} from "../../../__generated__/api";
import Avatar from "../../avatar/Avatar";
import DisplayUser from "../../creators/user/User";
import { ReplacementGroup } from "../../replacement-group";
import Select from "../../select/AsyncSelect";
import { Option, OptionWithChildren, SelectInstanceHandler } from "../../select/Select";

interface UserData {
    replacement?: FormFieldReplacementInput;
    user?: User;
}
export interface UsersPickerProps<TIsMulti = false, TIsClearable=false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    className?: string;
    isClearable?: TIsClearable;
    canCreate?: boolean;
    replacements?: FormFieldReplacementInput[];
    input?: ReplacementGroup[] | undefined;
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (
        sourceId: (TIsMulti extends true ? string[] : string) | (TIsClearable extends true ? undefined : never),
        text?: (TIsMulti extends true ? string[] : string),
        replacements?: FormFieldReplacementInput[]
    ) => void;
}

function getLabel(obj: Option<UserData>) {
    return obj.data?.replacement?.replacement ?? obj.data?.user?.name;
}
const Users = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, className, isClearable, input, replacements, canCreate,
// eslint-disable-next-line @typescript-eslint/no-unused-vars
}: UsersPickerProps<TIsMulti, TIsClearable>, _ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const client = useApolloClient();

    const [isShowCreate, setIsShowCreate] = useState(false);

    const { data, loading, error } = useSearchUsersQuery({
        skip: !value,
        variables: {
            ids: Array.isArray(value) ? value : [value],
        },
    });

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true
        ? readonly Option<UserData>[]
        : Option<UserData>;
    type ValueType = TIsMulti extends true
        ? Option<UserData>[]
        : Option<UserData>;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let values = data?.users?.map(user => ({
        label: (
            <>
                <Avatar src={user.avatarUrl as string} variant="xs" />
                {user.name}
            </>
        ),
        value: user.id.toString() as string,
    })) as (OptionWithChildren<UserData> | Option<UserData>)[] ?? [];

    const expectedType = isMulti
        ? ((type: FormFieldType) => type === FormFieldType.Users || type === FormFieldType.User)
        : ((type: FormFieldType) => type === FormFieldType.User);
    const userInputs = input?.filter(row => row.input?.some(inputValue => expectedType(inputValue.type)));
    let replaceInput: OptionWithChildren<UserData>[] | undefined;
    if (userInputs?.isNotEmpty) {
        replaceInput = userInputs.map(group => ({
            label: group.label,
            options: group.input?.filter(inputType => expectedType(inputType.type)).map(inputType => ({
                label: `${group.label}/${inputType.name}`,
                value: `${group.id}/${inputType.id}`,
                data: {
                    replacement: {
                        fieldId: inputType.id,
                        groupId: group.id,
                        replacement: `${group.id}/${inputType.id}`,
                    } as FormFieldReplacementInput,
                },
            })),
        } as OptionWithChildren<UserData>));

        values = replaceInput.concat([{
            label: "Users",
            options: values as Option<UserData>[],
        }]);
    }

    const handleSelected = (selected: SelectedType) => {
        if (!selected) {
            (onSelected as (empty: unknown) => void)(undefined);
            return;
        }

        if ((Array.isArray(selected) && selected.find(row => row.value === "new"))
            || (selected as Option<UserData>).value === "new") {
            setIsShowCreate(true);
            if (Array.isArray(selected)) {
                // eslint-disable-next-line no-param-reassign
                selected = selected.filter(row => row.value !== "new") as typeof selected;
            } else {
                return;
            }
        }

        const vals = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<UserData>).value) as Parameters<typeof onSelected>[0];

        const texts = (Array.isArray(selected)
            ? selected.map(getLabel)
            : getLabel(selected as Option<UserData>)) as Parameters<typeof onSelected>[1];

        const newReplace = (Array.isArray(selected)
            ? selected.map(row => (row as Option<UserData>).data?.replacement)
            : [(selected as Option<UserData>).data?.replacement]).filterNull();
        onSelected(vals, texts, newReplace);
    };

    const replacementMap = replacements ? replacements.toIdMap("replacement", row => `${row.groupId}/${row.fieldId}`) : {};

    const valueMap = (values as Option<UserData>[])
        .concat((values as OptionWithChildren<UserData>[]).flatMap(row => row.options))
        .filter(row => row.value)
        .toIdMap("value");
    const selected = Array.isArray(value)
        ? value.map(row => valueMap[replacementMap[row] ?? row])
        : valueMap[replacementMap[value as string] ?? value as string];

    const loadOptions = async (query: string) => {
        const results = await client.query({
            query: SearchUsersDocument,
            variables: {
                query,
            },
        });

        const resultValues = (results.data as SearchUsersQuery).users?.map(user => ({
            label: (
                <>
                    <Avatar src={user.avatarUrl as string} variant="xs" />
                    {user.name}
                </>
            ),
            value: user.id as string,
            data: {
                user,
            },
        } as Option<UserData>)) ?? [];

        if (canCreate && !query) {
            return [
                {
                    label: "Create",
                    options: [
                        {
                            label: "Create New User",
                            value: "new",
                        },
                    ],
                },
                {
                    label: "Users",
                    options: resultValues,
                },
            ];
        }

        if (replaceInput) {
            return resultValues.isEmpty ? replaceInput : replaceInput.concat([
                {
                    label: "Users",
                    options: resultValues,
                },
            ]);
        }

        return resultValues;
    };

    return (
        <>
            {isShowCreate && (

                <DisplayUser
                    onHide={() => setIsShowCreate(false)}
                    onSave={user => {
                        if (isMulti && Array.isArray(values)) {
                            // Nothing for now
                        } else {
                            onSelected(user.id, user.name as Parameters<typeof onSelected>[1]);
                        }

                        setIsShowCreate(false);
                    }}
                />
            )}
            <Select
            // ref={ref}
                minRequired={minRequired}
                placeholder={placeholder}
                isMulti={isMulti}
                onChange={handleSelected}
                value={selected as ValueType}
                loadOptions={loadOptions}
                className={className}
                isClearable={isClearable}
            />
        </>
    );
});

Users.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    className: undefined,
    isClearable: undefined,
    input: undefined,
    replacements: undefined,
    canCreate: false,
};

export default Users as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false
> (
    props: UsersPickerProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof Users>;
